// ============================================================================
// TurnstileWidget — wraps Cloudflare Turnstile so forms can request a
// bot-check token before submitting.
//
// Usage:
//   const [token, setToken] = useState(null);
//   <TurnstileWidget onVerify={setToken} />
//   // then include { turnstileToken: token } in the POST body
//
// Behaviour:
//   - If REACT_APP_TURNSTILE_SITE_KEY is not set, the component renders
//     nothing and never calls onVerify. The backend middleware is configured
//     to pass through in that case, so soft-launch keeps working.
//   - The Cloudflare script (challenges.cloudflare.com/turnstile/v0/api.js)
//     must be loaded once on the page. We inject it on first mount if it
//     isn't there already; you can also drop a <script> tag in index.html.
//   - Auto-resets after 5 minutes (Turnstile tokens expire).
// ============================================================================
import React, { useEffect, useRef, useState } from 'react';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SCRIPT_ID = 'cf-turnstile-script';

function ensureScript() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SCRIPT_ID)) return;
  const s = document.createElement('script');
  s.id = SCRIPT_ID;
  s.src = SCRIPT_URL;
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}

const TurnstileWidget = ({ onVerify, action, theme = 'auto' }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  // Turnstile site key — public value, safe to commit to source.
  // Cloudflare site keys appear in the browser's JS bundle anyway.
  // To rotate: get a new site key from Cloudflare Turnstile dashboard,
  // paste below as the fallback. No env-var management needed.
  const [siteKey] = useState(
    process.env.REACT_APP_TURNSTILE_SITE_KEY ||
    '0x4AAAAAADj-p1y3GpwHJlaQ'
  );

  useEffect(() => {
    if (!siteKey) return undefined;
    ensureScript();

    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      const ts = window.turnstile;
      if (!ts || !containerRef.current) {
        // Script still loading; retry shortly
        setTimeout(tryRender, 200);
        return;
      }
      if (widgetIdRef.current != null) return;
      try {
        widgetIdRef.current = ts.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          action,
          callback: (token) => {
            if (typeof onVerify === 'function') onVerify(token);
          },
          'error-callback': () => {
            if (typeof onVerify === 'function') onVerify(null);
          },
          'expired-callback': () => {
            if (typeof onVerify === 'function') onVerify(null);
          },
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[turnstile] render failed:', e?.message);
      }
    };
    tryRender();

    return () => {
      cancelled = true;
      try {
        if (window.turnstile && widgetIdRef.current != null) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch {
        // ignore
      }
      widgetIdRef.current = null;
    };
  }, [siteKey, action, theme, onVerify]);

  if (!siteKey) return null;
  return <div ref={containerRef} style={{ margin: '12px 0' }} />;
};

export default TurnstileWidget;
